class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };

    // Detect if current customer is special customer
    this.isSpecialCustomer = this.checkIfSpecialCustomer();
  }

  /**
   * Get special customer tags from theme settings
   * Returns array of special customer tag strings
   */
  getSpecialCustomerTags() {
    // Get from theme settings data attribute
    const themeSettings = document.querySelector('[data-customer-b2b-tags]');
    if (themeSettings) {
      const tagsString = themeSettings.dataset.customerB2bTags;
      if (tagsString) {
        // Parse tags: "special-customer, super-customer" -> ["special-customer", "super-customer"]
        return tagsString.split(',').map(tag => tag.trim().toLowerCase()).filter(tag => tag);
      }
    }

    // Fallback: check for global variable
    if (window.specialCustomerTags && Array.isArray(window.specialCustomerTags)) {
      return window.specialCustomerTags.map(tag => tag.toLowerCase());
    }

    // Default empty array
    return [];
  }

  /**
   * Check if the current customer is a special customer
   * Returns true if customer has any of the special tags from settings
   */
  checkIfSpecialCustomer() {
    const specialTags = this.getSpecialCustomerTags();
    
    // If no special tags configured, all customers are regular
    if (specialTags.length === 0) {
      return false;
    }

    // Method 1: Check dari window/global variable (jika tersedia)
    if (window.customerTags && Array.isArray(window.customerTags)) {
      return window.customerTags.some(tag => 
        specialTags.includes(tag.toLowerCase())
      );
    }

    // Method 2: Check dari meta tag
    const customerMetaTag = document.querySelector('meta[name="customer-tags"]');
    if (customerMetaTag) {
      const customerTagsStr = customerMetaTag.getAttribute('content').toLowerCase();
      return specialTags.some(specialTag => customerTagsStr.includes(specialTag));
    }

    // Method 3: Check dari data attribute di body atau html
    const bodyTags = (document.body.dataset.customerTags || document.documentElement.dataset.customerTags || '').toLowerCase();
    if (bodyTags) {
      return specialTags.some(specialTag => bodyTags.includes(specialTag));
    }

    // Method 4: Check dari customer object (Shopify)
    if (typeof Shopify !== 'undefined' && Shopify.customer && Shopify.customer.tags) {
      return Shopify.customer.tags.some(tag => 
        specialTags.includes(tag.toLowerCase())
      );
    }

    // Default: customer biasa (non-special)
    return false;
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();
    
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        // console.log('url :', url)
        // console.log(responseText)
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }
  
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        // Filter products based on customer type
        this.filterProductsByCustomerType(contentElement);
        
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
     
              } else {
                console.error(`Element with id '${id}' not found in the parsed response.`);
              }
            } else {
              console.error(`Element with id '${id}' not found in the document.`);
            }
          }

  /**
   * Filter products based on customer type
   * - Special customers: only see special products
   * - Regular customers: only see regular (non-special) products
   */
  filterProductsByCustomerType(contentElement) {
    const productItems = contentElement.querySelectorAll(
      '.predictive-search__item, .search-result__item, [data-product-item]'
    );
    
    productItems.forEach(item => {
      const isSpecialProduct = this.isProductSpecial(item);
      
      if (this.isSpecialCustomer) {
        // Special customer: hide non-special products
        if (!isSpecialProduct) {
          item.remove();
        }
      } else {
        // Regular customer: hide special products
        if (isSpecialProduct) {
          item.remove();
        }
      }
    });
    
    // Update result counts after filtering
    this.updateResultCounts(contentElement);
  }

  /**
   * Check if a product is a special product
   * Returns true if product has special/customer tags
   */
  isProductSpecial(item) {
    // Check 1: Direct attributes on item
    if (item.hasAttribute('data-special-product') || 
        item.hasAttribute('data-customer-tags') ||
        item.dataset.specialProduct === 'true') {
      return true;
    }
    
    // Check 2: Nested element with special product attribute
    if (item.querySelector('[data-special-product="true"]')) {
      return true;
    }
    
    // Check 3: Product card metafield data
    const productCard = item.querySelector('.product-card, [data-product-id]');
    if (productCard) {
      const metafieldData = productCard.dataset.metafields || 
                           productCard.dataset.customMetafields;
      
      if (metafieldData) {
        // Check for customer tags metafield
        if (metafieldData.includes('custom_item_by_customer_tags') ||
            metafieldData.includes('customer_tags') ||
            metafieldData.includes('special_customer')) {
          return true;
        }
      }
      
      // Check for special product class
      if (productCard.classList.contains('special-product') ||
          productCard.classList.contains('customer-specific')) {
        return true;
      }
    }
    
    // Check 4: Product tags in data
    const productTags = item.dataset.tags || item.dataset.productTags;
    if (productTags) {
      const tags = productTags.toLowerCase();
      if (tags.includes('special') || 
          tags.includes('vip') || 
          tags.includes('customer-specific')) {
        return true;
      }
    }
    
    return false;
  }
  
  /**
   * Update result count after filtering
   */
  updateResultCounts(contentElement) {
    const countElements = contentElement.querySelectorAll('[data-result-count], .search-result-count');
    const remainingProducts = contentElement.querySelectorAll(
      '.predictive-search__item, .search-result__item'
    ).length;
    
    countElements.forEach(el => {
      if (el.textContent.match(/\d+/)) {
        el.textContent = el.textContent.replace(/\d+/, remainingProducts);
      }
    });
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);